/**
 * Resolve list focus page from SDK service (handles services that omit `focus` from getAll wrapper).
 * @param {{ locateInList?: Function, getAll?: Function, api?: { getAll?: Function } }} service
 * @param {{ focusEntityId: string, pageSize?: number, tenantId?: string, sortBy?: string, sortOrder?: string }} params
 */

/** @type {Map<string, Promise<{ focus?: object }>>} */
const inFlightFocusRequests = new Map();

function focusRequestKey(params) {
    const sortPart = params.sortBy
        ? `${params.sortBy}:${params.sortOrder ?? 'desc'}`
        : '';
    return `${params.tenantId ?? ''}:${params.focusEntityId ?? ''}:${params.pageSize ?? ''}:${sortPart}`;
}

async function resolveListFocusOnce(service, params) {
    const query = {
        focusEntityId: params.focusEntityId,
        pageSize: params.pageSize,
        tenantId: params.tenantId,
    };
    if (params.sortBy) {
        query.sortBy = params.sortBy;
        query.sortOrder = params.sortOrder ?? 'desc';
    }

    if (typeof service?.locateInList === 'function') {
        return service.locateInList(query);
    }

    if (typeof service?.getAll === 'function') {
        const body = await service.getAll(query);
        if (body?.focus) {
            return { focus: body.focus };
        }
    }

    if (typeof service?.api?.getAll === 'function') {
        const raw = await service.api.getAll(query);
        return { focus: raw?.focus ?? { found: false } };
    }

    return { focus: { found: false } };
}

export async function resolveListFocus(service, params) {
    const dedupeKey = focusRequestKey(params);
    const existing = inFlightFocusRequests.get(dedupeKey);
    if (existing) {
        return existing;
    }

    const promise = resolveListFocusOnce(service, params).finally(() => {
        if (inFlightFocusRequests.get(dedupeKey) === promise) {
            inFlightFocusRequests.delete(dedupeKey);
        }
    });

    inFlightFocusRequests.set(dedupeKey, promise);
    return promise;
}
